import { BreadcrumbJsonLd } from "next-seo"
import { Container, Box, Breadcrumbs as MaterialUIBreadcrumbs } from "@mui/material"

import Link from "./i18n/Link"

import Icon from "@mdi/react"
import { mdiHome } from "@mdi/js"

import { NEXT_PUBLIC_HOST } from "utils/envVariables"
import { SecureoBreadCrumb } from "@secureo/common/typings/Breadcrumb"

export interface BreadCrumb extends SecureoBreadCrumb {
	label: string
	href: string
}

export const BreadCrumbStructuredData = ({
	ancestors = [],
	currentPath,
	locale,
}: BreadCrumbStructuredDataProps) => {
	const rootBreadCrumb: BreadCrumb = {
		label: "Digitalzylinder Shop",
		href: `/`,
	}

	const itemListElements = [rootBreadCrumb, ...ancestors, currentPath].map(
		({ label, href }, i) => {
			return {
				position: i + 1,
				name: label,
				item: `${NEXT_PUBLIC_HOST}/${locale}${href}`,
			}
		},
	)

	return <BreadcrumbJsonLd itemListElements={itemListElements} />
}

interface BreadCrumbStructuredDataProps {
	ancestors: SecureoBreadCrumb[]
	currentPath: SecureoBreadCrumb
	locale: string
}

export const getCountryNameFromLocale = (locale: string) => {
	switch (locale.toLowerCase()) {
		case "de-at":
			return "AT"
		case "de-ch":
			return "CH"

		case "de-de":
		default:
			return "DE"
	}
}

const breadCrumbCharacterLengthLimit = 50

const BreadCrumbs = ({ ancestors = [], currentPath, locale }: Props) => {
	const isMobile = false

	const homePageBreadCrumbLabel = `Shop ${getCountryNameFromLocale(locale)}`
	const totalBreadCrumbCharacterLength = [
		homePageBreadCrumbLabel,
		...ancestors.map(({ label }) => label),
		currentPath.label,
	].join("").length

	const mobileBreadcrumb = ancestors.length > 0 ? ancestors[ancestors.length - 1] : null

	return (
		<Box style={{ paddingBottom: 5 }}>
			<Container>
				<MaterialUIBreadcrumbs aria-label="breadcrumb" style={{ fontSize: "0.9rem" }}>
					{!isMobile && (
						<Link href="/">
							<div
								style={{
									display: "flex",
									alignItems: "center",
								}}
							>
								<Box>
									<Icon
										path={mdiHome}
										size={0.8}
										color="#4691CE"
										style={{ marginRight: 4, marginTop: 4 }}
									/>
								</Box>
								<Box>
									<strong>{homePageBreadCrumbLabel}</strong>
								</Box>
							</div>
						</Link>
					)}

					{!isMobile &&
						ancestors.map((path) => {
							const { label, href, ...props } = path

							return (
								<Link key={href} href={href} {...(props as any)}>
									{label}
								</Link>
							)
						})}

					{isMobile && mobileBreadcrumb && (
						<Link
							key={mobileBreadcrumb.href}
							href={mobileBreadcrumb.href}
							{...(mobileBreadcrumb as any)}
						>
							{mobileBreadcrumb.label}
						</Link>
					)}

					{!isMobile && (
						<span style={{ fontSize: "0.9rem" }}>
							{totalBreadCrumbCharacterLength > breadCrumbCharacterLengthLimit
								? "…"
								: currentPath.label}
						</span>
					)}
				</MaterialUIBreadcrumbs>
			</Container>
			<BreadCrumbStructuredData
				ancestors={ancestors}
				currentPath={currentPath}
				locale={locale}
			/>
		</Box>
	)
}

interface Props {
	ancestors?: SecureoBreadCrumb[]
	currentPath: SecureoBreadCrumb
	locale: string
}

export default BreadCrumbs
